import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useSiteContent } from "@/context/SiteContentContext";
import { artistDisciplines, splitByRole } from "@/lib/artist";
import MediaRenderer, { derivePreset } from "@/components/MediaRenderer";
import { schemeAccentHex, schemeHex } from "@/lib/schemes";
import Seo from "@/components/Seo";

/**
 * Artists page — center-aligned, image-first, no cards / no roster feel.
 * Filters are the artists' own disciplines (dj, producer, hip hop, …), not editorial categories.
 * Every tile shows an experimentally-rendered version of the artist's media, hover reveals the original.
 * Page-level toggles for kicker/title/intro live in admin → "site" → sub-tab "artists page".
 */
export default function Artists() {
    const { t, lang } = useT();
    const { content } = useSiteContent();
    const [artists, setArtists] = useState([]);
    const [active, setActive] = useState("all"); // discipline slug or "all"

    useEffect(() => {
        api.get("/artists").then((r) => setArtists(Array.isArray(r.data) ? r.data : []));
    }, []);

    const { artists: roster } = useMemo(() => splitByRole(artists), [artists]);

    // Discipline pills — built from the artists themselves, lowercased, deduped.
    const disciplinesList = useMemo(() => {
        const map = new Map();
        roster.forEach((a) => {
            artistDisciplines(a).forEach((raw) => {
                const key = String(raw || "").trim().toLowerCase();
                if (!key) return;
                map.set(key, (map.get(key) || 0) + 1);
            });
        });
        return Array.from(map.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([label, count]) => ({ label, count }));
    }, [roster]);

    const filtered = useMemo(() => {
        if (active === "all") return roster;
        return roster.filter((a) =>
            artistDisciplines(a).map((d) => String(d).toLowerCase()).includes(active)
        );
    }, [roster, active]);

    // Admin-controlled page copy (fallback to i18n)
    const showKicker = content.artists_page_show_kicker !== "0";
    const showTitle = content.artists_page_show_title !== "0";
    const showIntro = content.artists_page_show_intro !== "0";
    const kicker = content[`artists_page_kicker_${lang}`] || content.artists_page_kicker_en || t("artist_roster_kicker");
    const title = content[`artists_page_title_${lang}`] || content.artists_page_title_en || t("nav_artists");
    const intro = content[`artists_page_intro_${lang}`] || content.artists_page_intro_en || t("artist_intro");

    // Header block auto-hides when empty. `hasHeaderContent` = at least one of
    // kicker/title/intro is visible; if none, the section below (pills) needs
    // its OWN top padding so it doesn't collide with the site header.
    const hasHeaderContent = (showKicker && kicker) || (showTitle && title) || (showIntro && intro);
    const headerBlockVisible = hasHeaderContent && (showKicker || showTitle || showIntro);

    return (
        <div data-testid="artists-page">
            <Seo
                title="Artists — roster | ITZKATU"
                description="el roster de itzkatu: djs, productores, artistas visuales y más — colectivo creativo de san cristóbal de las casas."
                path="/artists"
            />
            {/* Page header — everything centered, quiet */}
            {headerBlockVisible && (
                <section className="px-4 md:px-8 pt-14 md:pt-20 pb-10 md:pb-14">
                    <div className="max-w-2xl mx-auto text-center">
                        {showKicker && kicker && (
                            <div className="font-mono text-[10px] uppercase tracking-[0.4em] opacity-60 mb-4">
                                {kicker}
                            </div>
                        )}
                        {showTitle && title && (
                            <h1 className="font-heading font-black text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9]">
                                {title.replace(/\.$/, "")}<span className="text-[color:var(--sc-accent)]">.</span>
                            </h1>
                        )}
                        {showIntro && intro && (
                            <p className="mt-6 font-body text-sm md:text-base leading-relaxed opacity-80 max-w-lg mx-auto">
                                {intro}
                            </p>
                        )}
                    </div>
                </section>
            )}

            {/* Discipline hashtags — Ksawery-style. "all" has no #, active is
                underlined, hover opacity. Bigger font than the previous pills so
                the taxonomy reads as content, not chrome. */}
            {disciplinesList.length > 0 && (
                <section className={`px-4 md:px-8 pb-10 ${headerBlockVisible ? "" : "pt-14 md:pt-20"}`}>
                    <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-center">
                        <DisciplineHashtag
                            active={active === "all"}
                            onClick={() => setActive("all")}
                            testId="artists-pill-all"
                        >
                            all
                        </DisciplineHashtag>
                        {disciplinesList.map(({ label }) => (
                            <DisciplineHashtag
                                key={label}
                                active={active === label}
                                onClick={() => setActive(label)}
                                testId={`artists-pill-${label.replace(/\s+/g, "-")}`}
                                hashtag
                            >
                                {label}
                            </DisciplineHashtag>
                        ))}
                    </div>
                </section>
            )}

            {/* Grid — image-only tiles, effect applied by default, hover reveals original */}
            <section className="px-4 md:px-8 pb-24 md:pb-32">
                <div className="max-w-6xl mx-auto">
                    {filtered.length === 0 ? (
                        <div className="py-24 text-center font-mono text-[10px] uppercase tracking-[0.35em] opacity-50">
                            {t("no_results")}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8 md:gap-x-16 lg:gap-x-28 xl:gap-x-36 md:gap-y-12">
                            {filtered.map((a) => (
                                <ArtistTile key={a.id} artist={a} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

function DisciplineHashtag({ active, onClick, testId, hashtag = false, children }) {
    // Hashtag-style filter — big, editorial. No borders, no bg, just typography.
    // Active state is underlined + full opacity; idle is dimmed. Matches the
    // "all #installation #show #musicvideo …" pattern from the reference site.
    const label = hashtag ? `#${String(children).replace(/\s+/g, "").toLowerCase()}` : children;
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`font-body text-lg md:text-2xl leading-tight transition-opacity ${
                active
                    ? "opacity-100 underline underline-offset-4 decoration-2"
                    : "opacity-50 hover:opacity-100 no-underline"
            }`}
        >
            {label}
        </button>
    );
}

export function ArtistTile({ artist }) {
    const { content: siteContent } = useSiteContent();
    const [hover, setHover] = useState(false);
    const disc = artistDisciplines(artist).join(" · ") || artist.discipline || "";
    const cover = artist.image_url_alt || artist.image_url || "";
    // Admin can hide discipline and/or location from the tile caption.
    // Defaults to visible ("1"). Set to "0" in site_content to hide.
    const showDiscipline = siteContent.artist_tile_show_discipline !== "0";
    const showLocation = siteContent.artist_tile_show_location !== "0";
    // Explicit render_preset from admin wins; else derive from slug.
    const preset = artist.render_preset && artist.render_preset !== ""
        ? artist.render_preset
        : derivePreset(artist.slug || artist.name || "");
    const isInterpretation = preset !== "original" && preset !== "";
    // Per-artist scheme — matches SceneSection's per-feature behavior. When an
    // artist has an assigned scheme we override the tile's CSS vars so canvas
    // effects, hover tints and caption accents all live in the artist's OWN
    // color world. Without an assigned scheme the tile inherits the page palette.
    // Global switch (admin → home sections) — governs COLOR INHERITANCE for all
    // effects: ON = tiles tinted (per-artist scheme wins, else page palette);
    // OFF = original colors.
    const fxInherit = siteContent.effects_inherit_scheme !== "0";
    const artistScheme = fxInherit ? (artist.scheme || "") : "";
    const schemeAccent = artistScheme ? schemeAccentHex(artistScheme) : "";
    const schemeVarStyle = artistScheme ? (() => {
        const hex = schemeHex(artistScheme);
        return {
            "--sc-bg": hex.bg,
            "--sc-text": hex.text,
            "--sc-subtext": hex.subtext,
            "--sc-accent": hex.accent,
            "--sc-accent2": hex.accent2,
            "--sc-border": hex.border,
            "--sc-card": hex.card,
        };
    })() : {};
    // Accent for CSS tints — hex if per-artist scheme, else fall back to
    // ancestor CSS var (so the tile still inherits the section/page world).
    const tintAccent = schemeAccent || "var(--sc-accent)";

    return (
        <Link
            to={`/artists/${artist.slug}`}
            data-testid={`artist-card-${artist.slug}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className="group flex flex-col items-start text-left"
            style={schemeVarStyle}
        >
            <div className="w-full aspect-[4/5] overflow-hidden bg-[#111] relative">
                {/* Base — the interpretation. For "original" preset, duotone the
                    image via CSS using the artist's accent (or page fallback). */}
                <div
                    className={`absolute inset-0 transition-opacity duration-500 ${isInterpretation && hover ? "opacity-0" : "opacity-100"}`}
                    style={!isInterpretation && fxInherit ? { backgroundColor: tintAccent } : undefined}
                >
                    <div
                        className="w-full h-full"
                        style={!isInterpretation && fxInherit ? { filter: "grayscale(1) contrast(1.15)", mixBlendMode: "multiply" } : undefined}
                    >
                        <MediaRenderer
                            src={cover}
                            alt={artist.name}
                            preset={preset}
                            asset_url={artist.render_asset_url}
                            seed={artist.slug || artist.name}
                            dark
                            scheme_accent={schemeAccent}
                        />
                    </div>
                </div>
                {/* Reveal on hover — original tinted with the artist's accent. */}
                {isInterpretation && cover && (
                    <div className={`absolute inset-0 transition-opacity duration-500 ${hover ? "opacity-100" : "opacity-0"}`}>
                        <div className="relative w-full h-full">
                            <img
                                src={artist.image_url || cover}
                                alt=""
                                aria-hidden="true"
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                )}
            </div>
            <h3 className="mt-4 md:mt-5 font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95] text-current group-hover:opacity-80 transition-opacity">
                {artist.name}
            </h3>
            {/* discipline (italic) + location — plain white/current text (not accent) */}
            {((showDiscipline && disc) || (showLocation && artist.location)) && (
                <div className="mt-1 font-body text-sm md:text-base text-current opacity-70 leading-snug">
                    {showDiscipline && disc && <div className="italic">{disc}</div>}
                    {showLocation && artist.location && <div>{artist.location}</div>}
                </div>
            )}
        </Link>
    );
}
